import React, { useState, useEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import { apiCall, API_BASE } from '../../api';
import { logger, getErrorMessage } from '../../utils/logger';
import { setI18nLanguage } from '../../i18n/config';
import i18n from '../../i18n/config';

const UserList = ({ ngo, onHome, onBack, onEditProfile }) => {
  const { user, getAccessTokenSilently, logout } = useAuth0();
  const { t } = useTranslation('userList');
  const { t: tc } = useTranslation('common');
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleted, setDeleted] = useState(false);
  const [loading, setLoading] = useState(true);

  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef(null);

  // ---- Language menu state ----
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const langMenuRef = useRef(null);

  useEffect(() => {
    if (!profileOpen) return;
    const handleClickOutside = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [profileOpen]);

  // Close the language menu on outside click.
  useEffect(() => {
    if (!langMenuOpen) return;
    const handleClickOutside = (e) => {
      if (langMenuRef.current && !langMenuRef.current.contains(e.target)) {
        setLangMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [langMenuOpen]);

  useEffect(() => {
    if (!ngo?.id) return;
    apiCall(`/api/organizations/${ngo.id}/members/`, {}, getAccessTokenSilently)
      .then(data => setUsers(data))
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  }, [ngo, getAccessTokenSilently]);

  const filtered = users.filter(u => {
    const fullName = `${u.first_name || ''} ${u.last_name || ''}`.trim();
    return (
      fullName.toLowerCase().includes(search.toLowerCase()) ||
      (u.email && u.email.toLowerCase().includes(search.toLowerCase())) ||
      (u.role_name && u.role_name.toLowerCase().includes(search.toLowerCase()))
    );
  });

  const handleDelete = async (id) => {
    try {
      await apiCall(`/api/organizations/${ngo.id}/members/${id}/`, { method: 'DELETE' }, getAccessTokenSilently);
      setUsers(users.filter(u => u.id !== id));
      setConfirmDelete(null);
      setDeleted(true);
      setTimeout(() => setDeleted(false), 2000);
    } catch (err) {
      logger.error('API', 'DELETE user failed', { status: err?.status, detail: err });
      alert(getErrorMessage(err, 'Failed to delete user.'));
    }
  };

  const handleLogout = () => {
    setProfileOpen(false);
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  const handleEditProfile = () => {
    setProfileOpen(false);
    if (onEditProfile) onEditProfile();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top bar — DESIGN.md §2 (Pattern A: py-4 / gap-3 / flex-1 spacer) */}
      <div className="bg-white border-b border-gray-200 px-4 py-4 flex items-center gap-3">
        {onHome && (
          <button onClick={onHome} aria-label="Home" title="Home" className="text-blue-600 hover:text-blue-700 flex-shrink-0">
            <svg width="20" height="20" viewBox="0 0 24 24" className="fill-current">
              <path d="M12 3.2 3 10v10h6v-6h6v6h6V10l-9-6.8Z" />
            </svg>
          </button>
        )}
        <button onClick={onBack} aria-label="Back" className="text-blue-600 hover:text-blue-700">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path d="M19 12H5M5 12l7-7M5 12l7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
        <span className="text-sm font-medium text-gray-900 flex-1">{t('title')}</span>

        <div className="flex-1" />

        {/* ---- LANGUAGE TOGGLE ---- */}
        <div ref={langMenuRef} className="relative">
          <button
            onClick={() => setLangMenuOpen((open) => !open)}
            aria-label="Language menu"
            aria-expanded={langMenuOpen}
            className="text-xs font-medium text-gray-600 border border-gray-200 rounded-lg px-2.5 py-1.5 hover:bg-gray-50 transition"
          >
            {i18n.language === 'ml' ? 'മലയാളം' : 'EN'}
          </button>
          {langMenuOpen && (
            <div className="absolute right-0 top-10 w-32 bg-white rounded-xl shadow-lg border border-gray-100 p-2 z-50">
              <button
                onClick={() => {
                  const lang = 'en';
                  setI18nLanguage(lang);
                  localStorage.setItem('zbookkeeping_language', lang);
                  setLangMenuOpen(false);
                  // Persist to backend (fire-and-forget)
                  getAccessTokenSilently({
                    authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE },
                  }).then(token => {
                    fetch(`${API_BASE || ''}/api/accounts/profile/update/`, {
                      method: 'PATCH',
                      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                      body: JSON.stringify({ preferred_language: lang }),
                    }).catch(() => {});
                  }).catch(() => {});
                }}
                className={`w-full text-left rounded-lg px-3 py-2 text-sm transition ${
                  i18n.language === 'en' ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                English
              </button>
              <button
                onClick={() => {
                  const lang = 'ml';
                  setI18nLanguage(lang);
                  localStorage.setItem('zbookkeeping_language', lang);
                  setLangMenuOpen(false);
                  // Persist to backend (fire-and-forget)
                  getAccessTokenSilently({
                    authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE },
                  }).then(token => {
                    fetch(`${API_BASE || ''}/api/accounts/profile/update/`, {
                      method: 'PATCH',
                      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                      body: JSON.stringify({ preferred_language: lang }),
                    }).catch(() => {});
                  }).catch(() => {});
                }}
                className={`w-full text-left rounded-lg px-3 py-2 text-sm transition ${
                  i18n.language === 'ml' ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                മലയാളം
              </button>
            </div>
          )}
        </div>

        <div ref={profileRef} className="relative">
          <button
            onClick={() => setProfileOpen((open) => !open)}
            aria-label="Account menu"
            aria-expanded={profileOpen}
            className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs font-medium"
          >
            {user?.name?.charAt(0).toUpperCase() || 'U'}
          </button>

          {profileOpen && (
            <div className="absolute right-0 top-11 w-64 bg-white rounded-xl shadow-lg border border-gray-100 p-4 z-50">
              <p className="text-sm text-gray-900 truncate">{user?.email}</p>
              <p className="text-sm text-gray-400 truncate mb-4">{user?.email}</p>

              <button
                onClick={handleEditProfile}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg py-2.5 text-sm font-medium transition mb-2"
              >
                {tc('editProfile')}
              </button>
              <button
                onClick={handleLogout}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg py-2.5 text-sm font-medium transition"
              >
                {tc('logout')}
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-sm mx-auto px-4 py-8">
        <h1 className="text-center text-base font-medium text-blue-600 mb-6 uppercase tracking-wider">
          {t('title')}
        </h1>

        <div className="mb-4">
          <input type="text" placeholder={tc('searchPlaceholder')} value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"/>
        </div>

        <div className="flex flex-col gap-3">
          {loading ? (
            <p className="text-sm text-gray-400 text-center py-4">{t('loading')}</p>
          ) : filtered.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-4">{t('noData')}</p>
          ) : (
            filtered.map(u => (
              <div key={u.id}
                className="bg-white border border-gray-200 rounded-xl p-4 flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-400">{u.role_name}</p>
                  <p className="text-sm font-medium text-gray-900">{`${u.first_name || ''} ${u.last_name || ''}`.trim() || t('noName')}</p>
                  <p className="text-xs text-gray-500">{u.email}</p>
                </div>
                <button onClick={() => setConfirmDelete(u)}
                  className="w-8 h-8 bg-red-100 hover:bg-red-200 rounded-lg flex items-center justify-center text-red-500 transition">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </button>
              </div>
            ))
          )}
        </div>

        {deleted && (
          <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-3 text-center">
            <p className="text-sm text-green-600 font-medium">{t('successMessage')}</p>
          </div>
        )}
      </div>

      {/* Confirm delete modal */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 mx-4 w-full max-w-xs text-center shadow-xl">
            <p className="text-sm font-medium text-gray-900 mb-1">{t('confirmTitle')}</p>
            <p className="text-sm font-bold text-red-500 mb-5">
              {t('confirmDelete', { id: confirmDelete.id })}
            </p>
            <div className="flex gap-3">
              <button onClick={() => handleDelete(confirmDelete.id)}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white rounded-lg py-2.5 text-sm font-medium transition">
                {t('confirmButton')}
              </button>
              <button onClick={() => setConfirmDelete(null)}
                className="flex-1 border border-gray-300 text-gray-600 hover:bg-gray-50 rounded-lg py-2.5 text-sm font-medium transition">
                {t('cancelButton')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserList;